
import { Jsonld } from "nuxt-jsonld";
import { scrollIntoView } from "seamless-scroll-polyfill";

import { Component, Vue, Watch, namespace } from "nuxt-property-decorator";
import FederalStates from "~/assets/data/federalStates";
import EsyH4 from "~/components/atoms/text/EsyH4.vue";
import RouterLink from "~/components/atoms/text/RouterLink.vue";
import AppStoreBadge from "~/components/molecules/display/badges/AppStoreButton.vue";
import FacebookIcon from "~/components/molecules/display/badges/FacebookIcon.vue";

import PlayStoreBadge from "~/components/molecules/display/badges/PlayStoreButton.vue";
import TwitterIcon from "~/components/molecules/display/badges/TwitterIcon.vue";
import SummedRating from "~/components/molecules/display/ratings/SummedRating.vue";
import { PelletContentPagesList } from "~/defaults/pellets/pelletsContentPages";
import { PelletServicePagesList } from "~/defaults/pellets/pelletsServicePages";
import { PaymentTypes, Products } from "~/operations/messages";
import {
  decodeUrlForPlace,
  encodePlaceNameSegment,
  getMkLogin,
  getPkLogin,
} from "~/operations/shared";
import { isTestRatingUrl } from "~/utils/test-rating-urls";

const EsyoilStore = namespace("esyoil");

type MenuLink = {
  name: string;
  title?: string;
  href?: string | { path: string };
  externalLink?: boolean;
  target?: "_blank";
};

@Jsonld
@Component({
  components: {
    RouterLink,
    SummedRating,
    AppStoreBadge,
    PlayStoreBadge,
    FacebookIcon,
    TwitterIcon,
    EsyH4,
  },
  methods: {
    decodeUrlForPlace,
    isTestRatingUrl,
    getPkLogin,
    getMkLogin,
  },
})
export default class Footer extends Vue {
  @EsyoilStore.Action("fetchEsyoilRatingData")
  fetchEsyoilRatingData: any;

  @EsyoilStore.State("companyRating")
  companyRating: { ratingValue: number; ratingCount: number };

  Products = Products;
  PaymentTypes = PaymentTypes;
  FederalStates = FederalStates;
  PelletContentPagesList = PelletContentPagesList;
  PelletServicePagesList = PelletServicePagesList;

  async fetch() {
    if (!this.companyRating.ratingCount) {
      await this.fetchEsyoilRatingData();
    }
  }

  @Watch("$route")
  scrollTop() {
    // internal navigation within the ortssite
    // window.scrollTo not working in Safari 17.1 version. use polyfill instead
    scrollIntoView(document.body, {
      behavior: "smooth",
      block: "start",
      inline: "start",
    });
  }

  jsonld() {
    return {
      "@context": "http://schema.org",
      "@graph": [
        {
          "@type": "WebPage",
          "@id": `${process.env.MAIN_ESYOIL}${this.$route.path}`,
        },
        ...(!isTestRatingUrl(this.$route.path)
          ? [
              {
                "@type": "Product",
                name: "esyoil",
                brand: {
                  "@type": "Brand",
                  name: "esypellets - Aktuelle Pelletspreise vergleichen und Holzpellets kaufen",
                },
                review: {
                  "@type": "Review",
                  reviewRating: {
                    "@type": "Rating",
                    ratingValue: Number(
                      this.$store.state.esyoil.companyRating.ratingValue,
                    ),
                    bestRating: 5,
                  },
                  author: {
                    "@type": "Organization",
                    name: "agital.online GmbH",
                  },
                },
                aggregateRating: {
                  "@type": "AggregateRating",
                  bestRating: 5,
                  worstRating: 1,
                  ratingValue: Number(
                    this.$store.state.esyoil.companyRating.ratingValue,
                  ),
                  ratingCount: Number(
                    this.$store.state.esyoil.companyRating.ratingCount,
                  ),
                },
              },
            ]
          : []),
      ],
    };
  }

  kontoLinks: MenuLink[] = [
    {
      name: "Mein Konto",
      title: "Hier gelangst Du zu deinem Konto",
      href: getMkLogin(),
      externalLink: true,
    },
    {
      name: "Händler-Konto",
      title: "Hier gelangst Du zu Deinem esyoil Händler-Konto",
      href: getPkLogin(),
      externalLink: true,
    },
  ];

  esyoilLinks: MenuLink[] = [
    {
      name: "Vertrag widerrufen",
      title: "Vertrag widerrufen",
      href: { path: "/pelletspreise/widerruf" },
      externalLink: false,
    },
    {
      name: "Über uns",
      title: "Erfahre mehr über esyoil, als Unternehmen",
      href: { path: "/unternehmen/esyoil" },
      externalLink: false,
    },
    {
      name: "Jobs",
      title: "Erfahre mehr über Karriere-Möglichkeiten bei esyoil",
      href: "https://agital.online/#career",
      externalLink: true,
      target: "_blank",
    },
    {
      name: "AGB",
      title: "Zu unseren Allgemeine Geschäfts- und Nutzungsbedingungen",
      href: { path: "/unternehmen/agb" },
      externalLink: false,
    },
    {
      name: "Impressum",
      title: "Zum Impressum",
      href: { path: "/unternehmen/impressum" },
      externalLink: false,
    },
    {
      name: "Presse",
      title: "Zu unserem Infoangebot",
      href: { path: "/unternehmen/presse" },
      externalLink: false,
    },
    {
      name: "Datenschutz",
      title: "Zu unserer Datenschutzauskunft",
      href: { path: "/unternehmen/datenschutz" },
      externalLink: false,
    },
  ];

  cities = [
    {
      name: `Berlin`,
      title: "Berlin",
      href: {
        path: decodeUrlForPlace("pelletspreise-berlin-10115", "pelletspreise"),
      },
    },
    {
      name: `Frankfurt am Main`,
      title: "Frankfurt am Main",
      href: {
        path: `/pelletspreise/pelletspreise-${encodePlaceNameSegment(
          "Frankfurt am Main",
        )}-60306`,
      },
    },
    {
      name: `Essen`,
      title: "Essen",
      href: {
        path: decodeUrlForPlace("pelletspreise-essen-45127", "pelletspreise"),
      },
    },
    {
      name: `Hannover`,
      title: "Hannover",
      href: {
        path: decodeUrlForPlace(
          "pelletspreise-hannover-30159",
          "pelletspreise",
        ),
      },
    },
    {
      name: `Wuppertal`,
      title: "Wuppertal",
      href: {
        path: decodeUrlForPlace(
          "pelletspreise-wuppertal-42103",
          "pelletspreise",
        ),
      },
    },
    {
      name: "Hamburg",
      title: "Hamburg",
      href: {
        path: decodeUrlForPlace("pelletspreise-hamburg-20095", "pelletspreise"),
      },
    },
    {
      name: "Stuttgart",
      title: "Stuttgart",
      href: {
        path: decodeUrlForPlace(
          "pelletspreise-stuttgart-70173",
          "pelletspreise",
        ),
      },
    },
    {
      name: "Leipzig",
      title: "Leipzig",
      href: {
        path: decodeUrlForPlace("pelletspreise-leipzig-04103", "pelletspreise"),
      },
    },
    {
      name: "Nürnberg",
      title: "Nürnberg",
      href: {
        path: decodeUrlForPlace(
          "pelletspreise-nürnberg-90402",
          "pelletspreise",
        ),
      },
    },
    {
      name: "Bielefeld",
      title: "Bielefeld",
      href: {
        path: decodeUrlForPlace(
          "pelletspreise-bielefeld-33602",
          "pelletspreise",
        ),
      },
    },
    {
      name: "München",
      title: "München",
      href: {
        path: decodeUrlForPlace("pelletspreise-münchen-80331", "pelletspreise"),
      },
    },
    {
      name: "Düsseldorf",
      title: "Düsseldorf",
      href: {
        path: decodeUrlForPlace(
          "pelletspreise-düsseldorf-40210",
          "pelletspreise",
        ),
      },
    },
    {
      name: "Bremen",
      title: "Bremen",
      href: {
        path: decodeUrlForPlace("pelletspreise-bremen-27568", "pelletspreise"),
      },
    },
    {
      name: "Duisburg",
      title: "Duisburg",
      href: {
        path: decodeUrlForPlace(
          "pelletspreise-duisburg-47051",
          "pelletspreise",
        ),
      },
    },
    {
      name: "Bonn",
      title: "Bonn",
      href: {
        path: decodeUrlForPlace("pelletspreise-bonn-53111", "pelletspreise"),
      },
    },
    {
      name: "Köln",
      title: "Köln",
      href: {
        path: decodeUrlForPlace("pelletspreise-köln-50667", "pelletspreise"),
      },
    },
    {
      name: "Dortmund",
      title: "Dortmund",
      href: {
        path: decodeUrlForPlace(
          "pelletspreise-dortmund-44135",
          "pelletspreise",
        ),
      },
    },
    {
      name: "Dresden",
      title: "Dresden",
      href: {
        path: decodeUrlForPlace("pelletspreise-dresden-01067", "pelletspreise"),
      },
    },
    {
      name: "Bochum",
      title: "Bochum",
      href: {
        path: decodeUrlForPlace("pelletspreise-bochum-44787", "pelletspreise"),
      },
    },
    {
      name: "Münster",
      title: "Münster",
      href: {
        path: decodeUrlForPlace("pelletspreise-münster-48143", "pelletspreise"),
      },
    },
  ];
}
